import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Bell, ChevronDown, ChevronUp, X, Loader2 } from "lucide-react";
import dayjs from "dayjs";
import { usePublishedNoticesQuery } from "../../hooks/useNoticeQuery";
import { GetNoticeBadge } from "../../components/common/GetNoticeBadge";

// URLを検知して <a> タグに変換するヘルパー関数
const renderTextWithLinks = (text: string) => {
  if (!text) return null;
  
  // URLの正規表現パターン
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // テキストをURLとそれ以外に分割
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // URLに一致する部分は <a> タグとして返す
    if (part.match(urlRegex)) {
      return (
        <a 
          key={index} 
          href={part} 
          className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
          onClick={(e) => e.stopPropagation()} // ★重要：クリックしたときにお知らせパネルの開閉が誤動作するのを防ぐ
        >
          {part}
        </a>
      );
    }
    // それ以外はただのテキストとして返す
    return <span key={index}>{part}</span>;
  });
};

export const NotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [readIds, setReadIds] = useState<number[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // APIから公開中のお知らせを取得
  const { data: notices = [], isLoading } = usePublishedNoticesQuery();

  // 2. 初回マウント時に LocalStorage から既読IDを読み込む
  useEffect(() => {
    const saved = localStorage.getItem("read_notice_ids");
    if (saved) {
      setReadIds(JSON.parse(saved));
    }
  }, []);
  
  // 不要になった大昔の既読IDを自動的に掃除する機能
  useEffect(() => {
    if (notices.length > 0 && readIds.length > 0) {
      // 現在APIから返ってきている「有効なお知らせID」のリスト
      const activeNoticeIds = notices.map(n => n.id);
      
      // 既読IDリストの中から、有効なお知らせに存在するもの「だけ」を残す
      const cleanedReadIds = readIds.filter(id => activeNoticeIds.includes(id));

      // もし古いIDが削られて数が減っていたら、LocalStorage と State を上書きして綺麗にする
      if (cleanedReadIds.length !== readIds.length) {
        localStorage.setItem("read_notice_ids", JSON.stringify(cleanedReadIds));
        setReadIds(cleanedReadIds);
      }
    }
  }, [notices]);

  // 3. 未読数の計算
  const unreadCount = useMemo(() => {
    if (!notices) return 0;
    return notices.filter((n) => !readIds.includes(n.id)).length;
  }, [notices, readIds]);

  // 4. お知らせを開いた（詳細を見た）時の処理
  const toggleNotice = (id: number) => {
    // アコーディオンの開閉
    setExpandedId((prev) => (prev === id ? null : id));

    // 未読なら既読リストに追加し、LocalStorage に保存
    if (!readIds.includes(id)) {
      const newReadIds = [...readIds, id];
      setReadIds(newReadIds);
      localStorage.setItem("read_notice_ids", JSON.stringify(newReadIds));
    }
  };

  // // 属性に応じたバッジを生成するヘルパー関数
  // const getNoticeBadge = (type: number) => {
  //   switch (type) {
  //     case 1: // Important
  //       return <span className="bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap"> 重　要 </span>;
  //     case 2: // System
  //       return <span className="bg-orange-100 text-orange-800 border border-orange-200 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap">システム</span>;
  //     case 3: // Business
  //       return <span className="bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap"> 取　引 </span>;
  //     default: // Normal (0)
  //       return <span className="bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap">お知らせ</span>;
  //   }
  // };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2 text-slate-300 hover:text-white transition-colors focus:outline-none"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm border border-slate-800">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">お知らせ</h2>
              <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-800">
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {isLoading ? (
                <div className="flex justify-center p-8 text-gray-400">
                  <Loader2 className="animate-spin" size={32} />
                </div>
              ) : notices.length === 0 ? (
                <p className="p-8 text-center text-gray-500">現在お知らせはありません。</p>
              ) : (
                <div className="space-y-2">
                  {notices.map((notice) => {
                    const isRead = readIds.includes(notice.id);
                    const isExpanded = expandedId === notice.id;

                    return (
                      <div key={notice.id} className={`border rounded-md overflow-hidden ${isRead ? "bg-white" : "bg-blue-50/50"}`}>
                        <div
                          onClick={() => toggleNotice(notice.id)}
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex flex-col gap-1 pr-4">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{dayjs(notice.publishedAt).format("YYYY/MM/DD HH:mm")}</span>
                              {!isRead && (
                                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm">NEW</span>
                              )}
                            </div>
                            <span className={`text-sm ${isRead ? "font-medium text-gray-700" : "font-bold text-gray-900"}`}>
                              {GetNoticeBadge(notice.noticeType)}：
                              {notice.title}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="p-4 pt-0 border-t bg-white">
                            <div className="mt-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {renderTextWithLinks(notice.content)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};